/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
import React from 'react';
import { IRefinerSortProps, RefinerSortComponent } from './refiner-sort.component';

/**
 * Class names used in component
 */
const componentModalClassName = 'msc-refiner-modal';
const closeModalClassName = `${componentModalClassName}__close`;
const modalContentClassName = `${componentModalClassName}__content`;
const modalContentWrapperClassName = `${componentModalClassName}__wrapper`;
const modalContentWrapper2ClassName = `${componentModalClassName}__wrapper-internal`;
const componentModalToggleClassName = `${componentModalClassName}-toggle`;

/**
 * IRefinerModalToggleComponent interface.
 */
interface IRefinerModalToggleComponent extends IComponent<IRefinerModalToggleProps> {}

/**
 * IRefinerModalComponent interface.
 */
interface IRefinerModalComponent extends IComponent<IRefinerModalProps> {}

/**
 * IRefinerModalToggleProps interface.
 */
export interface IRefinerModalToggleProps extends IComponentProps<{}> {
    innerRef: React.RefObject<HTMLButtonElement> | undefined;
    id: string;
    text: string;
    ariaLabel: string;
    onClick(): void;
}

/**
 * IRefinerModalProps interface.
 */
export interface IRefinerModalProps extends IComponentProps<{}> {
    modalTitle?: string;
    modalCloseButtonText?: string;
    isOpen?: boolean;
    returnRef?: React.RefObject<HTMLButtonElement> | undefined;
    refinerSortProps?: IRefinerSortProps;
    refinerOptions?: React.ReactNode[];
    onToggle?(): void;
}

/**
 * Returns the modal.
 * @param  props - IRefinerModalProps.
 * @returns Modal node.
 */
const modalNode = (props: IRefinerModalProps) => {
    return (
        <Modal
            autoFocus
            applicationNode='renderPage'
            returnFocusRef={props.returnRef}
            isOpen={props.isOpen}
            toggle={props.onToggle}
            className={componentModalClassName}
            zIndex={1050}
        />
    );
};

/**
 * Returns the modal header.
 * @param  props - IRefinerModalProps.
 * @returns ModalHeader node.
 */
const modalHeaderNode = (props: IRefinerModalProps) => {
    return <ModalHeader toggle={props.onToggle}>{props.modalTitle}</ModalHeader>;
};

/**
 * Returns the modal footer.
 * @param  props - IRefinerModalProps.
 * @returns ModalFooter node.
 */
const modalFooterNode = (props: IRefinerModalProps) => {
    return (
        <ModalFooter>
            <Button onClick={props.onToggle} className={closeModalClassName}>
                {props.modalCloseButtonText}
            </Button>
        </ModalFooter>
    );
};

/**
 * RefinerToggle component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
export const RefinerModalToggleFunctionalComponent: React.FC<IRefinerModalToggleProps> = (props: IRefinerModalToggleProps) => {
    const { text, onClick, ariaLabel, innerRef, id } = props;
    return (
        <button id={id} className={componentModalToggleClassName} aria-label={ariaLabel} onClick={onClick} ref={innerRef}>
            {text}
        </button>
    );
};

/**
 * RefinerModal component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerModalFunctionalComponent: React.FC<IRefinerModalProps> = (props: IRefinerModalProps) => {
    return (
        <>
            {React.cloneElement(
                modalNode(props),
                {},
                modalHeaderNode(props),
                <ModalBody className={modalContentClassName}>
                    <div className={modalContentWrapperClassName}>
                        <div className={modalContentWrapper2ClassName}>
                            {props.refinerSortProps && <RefinerSortComponent {...props.refinerSortProps} />}
                            {props.refinerOptions &&
                                props.refinerOptions.map((item, index) => <React.Fragment key={index}>{item}</React.Fragment>)}
                        </div>
                    </div>
                </ModalBody>,
                modalFooterNode(props)
            )}
        </>
    );
};

/**
 * RefinerModalToggle component.
 */
export const RefinerModalToggleComponent: React.FunctionComponent<IRefinerModalToggleProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRefinerModalToggleComponent
>('Refiner-Modal-Toggle', {
    component: React.memo(RefinerModalToggleFunctionalComponent)
});

/**
 * RefinerModal component.
 */
export const RefinerModalComponent: React.FunctionComponent<IRefinerModalProps> = msdyn365Commerce.createComponent<IRefinerModalComponent>(
    'Refiner-Modal',
    {
        component: React.memo(RefinerModalFunctionalComponent)
    }
);
